<template>
    <b-overlay :show="is_load">
        <b-tabs v-if="customer" content-class="mt-3">
            <b-tab :title="__t('CLIENT_INFO')" active>
                <div class="row">
                    <div class="col-md-4">
                        <div class="card mb-3">
                            <div class="card-body">
                                <h5 class="frame-heading mb-3">{{__t('CLIENT_DETAILS')}}</h5>

                                <div class="mb-3">
                                    <div class="text-muted small">{{__t('NAME')}}</div>
                                    <div class="font-weight-bold">{{ customer.username || '—' }}</div>
                                </div>

                                <div class="mb-3">
                                    <div class="text-muted small">{{__t('PHONE')}}</div>
                                    <div class="font-weight-bold">{{ customer.phone || '—' }}</div>
                                </div>

                                <div class="mb-0" v-if="customer.sources && customer.sources.length > 0">
                                    <div class="text-muted small mb-1">{{__t('SOURCES')}}</div>
                                    <span
                                        class="badge badge-light border mr-1 mb-1"
                                        v-for="s in customer.sources"
                                        :key="s.provider"
                                    >{{ s.provider }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="card mb-3">
                            <div class="card-body text-center">
                                <div class="text-muted small">{{__t('ORDERS')}}</div>
                                <div class="h3 mb-0">{{ customer.orders.length }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="card mb-3">
                            <div class="card-body text-center">
                                <div class="text-muted small">{{__t('BONUSES')}}</div>
                                <div class="h3 mb-0">{{ customer.bonuses }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-tab>

            <b-tab :title="__t('FAVORITE_DISHES')">
                <div class="card">
                    <div class="card-body">
                        <div
                            class="d-flex align-items-center py-2 border-bottom"
                            v-for="dish in favorite_dishes"
                            :key="dish.title"
                        >
                            <span class="badge badge-light border mr-3">{{ dish.qty }}x</span>
                            <img v-if="dish.image" :src="dish.image" width="32" height="32" class="rounded mr-3" style="object-fit: cover;">
                            <div class="flex-grow-1">{{ dish.title }}</div>
                            <div class="font-weight-bold">{{ dish.total.toFixed(2) }}</div>
                        </div>

                        <div class="text-muted" v-if="favorite_dishes.length === 0">
                            {{__t('NO_ORDERS_YET')}}
                        </div>
                    </div>
                </div>
            </b-tab>

            <b-tab :title="__t('ORDER_HISTORY')">
                <div class="card">
                    <div class="card-body p-0">
                        <div
                            class="d-flex align-items-center py-3 px-3 order-history-row"
                            v-for="(o, idx) in customer.orders"
                            :key="o.id"
                            :class="{ 'border-bottom' : idx < customer.orders.length - 1 }"
                            @click="open_order(o)"
                        >
                            <div class="order-history-icon mr-3">
                                <img
                                    v-if="o.provider && o.provider.icon_url"
                                    :src="o.provider.icon_url"
                                    class="order_prov_image"
                                    alt=""
                                >
                                <i class="fal fa fa-file-o fa-file" v-else></i>
                            </div>

                            <div class="d-flex align-items-center product-stack mr-3" v-if="o.products && o.products.length > 0">
                                <img
                                    v-for="(p, pidx) in o.products.slice(0, 4)"
                                    :key="pidx"
                                    :src="p.image"
                                    class="product-stack-img"
                                    :style="{ zIndex: 10 - pidx }"
                                    alt=""
                                >
                                <div class="product-stack-more" v-if="o.products.length > 4">
                                    +{{ o.products.length - 4 }}
                                </div>
                            </div>

                            <div class="flex-grow-1 min-width-0 mr-3">
                                <div class="font-weight-bold text-truncate">
                                    {{ o.order_code }}
                                    <span class="badge badge-danger ml-2" v-if="o.is_rejected">{{__t('REJECTED')}}</span>
                                </div>
                                <small class="text-muted">{{ o.created_at }} · {{ o.product_count }} {{__t('ITEMS')}}</small>
                            </div>

                            <div class="font-weight-bold text-nowrap">{{ o.cost }}</div>
                        </div>

                        <div class="text-muted p-3" v-if="customer.orders.length === 0">
                            {{__t('NO_ORDERS_YET')}}
                        </div>
                    </div>
                </div>
            </b-tab>
        </b-tabs>

        <div class="text-muted text-center p-4" v-else-if="!is_load">
            {{__t('NO_CUSTOMER_ATTACHED')}}
        </div>
    </b-overlay>
</template>

<script>
export default {
    name: "order_client",
    props : {
        order : {
            type : Object
        }
    },
    data() {
        return {
            is_load: false,
            customer: null,
        }
    },
    created() {
        this.load_customer();
    },
    watch: {
        'order.id'(new_id, old_id) {
            if (new_id > 0 && new_id !== old_id) {
                this.load_customer();
            }
        },
    },
    computed: {
        favorite_dishes() {
            if (!this.customer || !Array.isArray(this.customer.orders)) {
                return [];
            }

            const map = {};
            this.customer.orders.forEach(order => {
                (order.products || []).forEach(p => {
                    const key = p.title;
                    if (!map[key]) {
                        map[key] = {title: p.title, image: p.image, qty: 0, total: 0};
                    }
                    map[key].qty += parseInt(p.qty) || 0;
                    map[key].total += (parseFloat(p.item_cost) || 0) * (parseInt(p.qty) || 0);
                });
            });

            return Object.values(map).sort((a, b) => b.qty - a.qty);
        },
    },
    methods : {
        load_customer() {
            this.is_load = true;
            this.customer = null;

            api.get('/internal/v1/catalog/orders/' + parseInt(this.order.id) + '/customer').then(({data}) => {
                this.is_load = false;
                if (data.status === "operation_success") {
                    this.customer = data.data;
                }
            }).catch(() => {
                this.is_load = false;
                this.customer = null;
            });
        },

        open_order(o) {
            EventBus.$emit("order_modal_secondary", {id: o.id});
        },
    },
}
</script>

<style scoped>
.order-history-row {
    cursor: pointer;
    transition: background-color .15s ease;
}

.order-history-row:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.order-history-icon {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.order_prov_image {
    display: block;
    width: 36px;
    height: 36px;
    object-fit: contain;
}

.min-width-0 {
    min-width: 0;
}

.product-stack-img,
.product-stack-more {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, .08);
    margin-left: -10px;
    flex: 0 0 auto;
}

.product-stack-img {
    object-fit: cover;
}

.product-stack-img:first-child {
    margin-left: 0;
}

.product-stack-more {
    background-color: #e4edff;
    color: #1a3fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
}
</style>
